import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { VStack, HStack, Heading, Text } from 'native-base';
import { Stack } from '@mui/material';
import {
  animated,
  useTransition,
  AnimatedProps,
  useSpringRef,
} from 'react-spring';

// Redux
import {
  closeModal,
  useAppDispatch,
  useGetCurrentUserAccountStatesQuery,
} from '@breakroom-nx/redux';

// Components
import {
  PermissionPanel,
  IconButton,
  ModalWrapper,
  PrimaryButton,
} from '../../';

// Assets
import { theme } from '@breakroom-nx/assets';
import {
  ArrowNarrowLeftIcon,
  ArrowNarrowRightIcon,
} from 'react-native-heroicons/solid';

import enable_cookies from '../../../../assets/src/images/enable_cookies.gif';
import enable_location from '../../../../assets/src/images/enable_location.gif';
import enable_notifications from '../../../../assets/src/images/enable_notifications.gif';

import { PermissionStatus, PermissionType } from '@breakroom-nx/helpers';

const panels: ((
  props: AnimatedProps<{ style: CSSProperties }>
) => React.ReactElement)[] = [
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <PermissionPanel
        type={PermissionType.Cookies}
        title={'Cookies'}
        description={
          'We believe in only asking for data that we need to give you the best experience, nothing more, nothing less. The only cookies we ask of you, are those that allow us to keep your session running in your browser and a few analytic ones that help us learn about our users and improve on building the best experience for you.'
        }
        image={enable_cookies}
        primaryButtonText={'I Understand'}
      />
    </animated.div>
  ),
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <PermissionPanel
        type={PermissionType.Location}
        title={'Location Services'}
        description={
          'This platform is centered around geolocation. You are in full control of your privacy and neither we, nor the users, will know your exact location. We only store your city and country and you can hide these details at any time.'
        }
        image={enable_location}
        primaryButtonText={'Decide on Location Services'}
        secondaryButtonText={'Not Right Now'}
      />
    </animated.div>
  ),
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <PermissionPanel
        type={PermissionType.Notifications}
        title={'Notifications'}
        description={
          "This platform involves taking to human beings. We don't want you to miss out on the fun by not knowing when there is a buzz in the air. Still, we understand how much we all need peace and quiet, so this decision is up to you!"
        }
        image={enable_notifications}
        primaryButtonText={'Decide on Notifications'}
        secondaryButtonText={'Not Right Now'}
      />
    </animated.div>
  ),
];

export const PermissionsContainer = () => {
  // Redux
  const dispatch = useAppDispatch();
  const [index, set] = useState(0);
  const { data: accountStates, isLoading: isLoadingAccountStates } =
    useGetCurrentUserAccountStatesQuery();

  const [finished, setFinished] = useState(false);
  const [rightClick, setRightClick] = useState(true);
  const onClick = useCallback(
    (right: boolean) =>
      set((state) => (right ? state + 1 : state - 1) % panels.length),
    []
  );
  const transRef = useSpringRef();
  const transitions = useTransition(index, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
      transform: `translate3d(${rightClick ? '100%' : '-100%'},0,0)`,
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
  });

  useEffect(() => {
    transRef.start();
  }, [index]);

  useEffect(() => {
    if (accountStates) {
      if (
        accountStates.cookiePermissions !== PermissionStatus.NotSet &&
        accountStates.locationPermissions !== PermissionStatus.NotSet &&
        accountStates.notificationPermissions !== PermissionStatus.NotSet
      ) {
        setFinished(true);
      }
    }
  }, [accountStates]);

  return (
    <ModalWrapper disabled>
      <VStack space={5}>
        <VStack
          bg={'surface.100'}
          space={3}
          textAlign={'center'}
          style={{
            borderColor: theme.colors.surface[200],
            borderWidth: 1,
            borderRadius: 25,
            shadowColor: theme.colors.surface[100],
            shadowOpacity: 0.25,
            shadowRadius: 20,
            shadowOffset: {
              width: 10,
              height: 20,
            },
            padding: 30,
          }}
        >
          <Heading fontSize={30} lineHeight={34}>
            The Boring Stuff
          </Heading>
          <Text color={'surface.600'} fontSize={16}>
            We want to quickly cover a few permissions we need before we get you
            into the action!
          </Text>
        </VStack>

        <VStack
          alignItems={'flex-start'}
          overflow={'hidden'}
          bg={'surface.100'}
          style={{
            borderColor: theme.colors.surface[200],
            borderWidth: 1,
            borderRadius: 25,
            shadowColor: theme.colors.surface[100],
            shadowOpacity: 0.25,
            shadowRadius: 20,
            shadowOffset: {
              width: 10,
              height: 20,
            },
            padding: 30,
          }}
        >
          <HStack
            width={'100%'}
            style={{
              // display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {transitions((style, i) => {
              const Page = panels[i];
              return <Page style={style} />;
            })}
          </HStack>
          <Stack
            width={'100%'}
            direction={'row-reverse'}
            justifyContent={'space-between'}
            alignItems={'flex-end'}
          >
            {index === panels.length - 1 ? (
              <PrimaryButton
                text={!finished ? 'Please Answer All Prompts' : 'Finish!'}
                onPress={() => {
                  dispatch(closeModal());
                }}
                disabled={isLoadingAccountStates || !finished}
                isLoading={isLoadingAccountStates}
              />
            ) : (
              <IconButton
                disabled={index === panels.length - 1}
                onPress={() => {
                  setRightClick(true);
                  onClick(true);
                }}
                icon={
                  <ArrowNarrowRightIcon
                    color={theme.colors.primary[400]}
                    size={20}
                  />
                }
              />
            )}

            <IconButton
              disabled={index === 0 || isLoadingAccountStates}
              onPress={() => {
                setRightClick(false);
                onClick(false);
              }}
              icon={
                <ArrowNarrowLeftIcon
                  color={theme.colors.primary[400]}
                  size={20}
                />
              }
            />
          </Stack>
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
